import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  DataGridPro,
  GridColDef,
  GridRowModel,
  GridRowModesModel,
  GridRowModes,
  GridRowId,
  GridEventListener,
  GridRowParams,
  MuiEvent,
  GridSelectionModel,
  GridLinkOperator,
} from '@mui/x-data-grid-pro'
import { ColumnsDef } from './DataGridColDef'
import { useTheme } from '@mui/material'
import { DragIndicator } from '@mui/icons-material'
import DataGridEditToolbar from './DataGridEditToolBar'
import { checkUserRoles } from '../lib/rolesAndPermissions'
import DeleteModal from './DeleteModal'
import { useAuth0 } from '@auth0/auth0-react'

const CustomMoveIcon = () => {
  return <DragIndicator color='secondary' />
}

const emptyMutation = () => ({
  mutate: null,
  isLoading: null,
})

const defaultToolbarConfig = {
  export: true,
  add: true,
  upload: true,
}

export const DataList = (props: any) => {
  const { user, logout } = useAuth0()
  const {
    columns: dataColumns,
    queryKey,
    useGetItems,
    useEditMutate = emptyMutation,
    useDeleteMutate = emptyMutation,
    useResendUserInvitation = emptyMutation,
    toolBarItems = defaultToolbarConfig,
    customToolBar,
    enableBulkActions = true,
    addAction,
    gridFilters,
    setGridFilters,
    isCellEditable = () => true,
    viewLink,
    includeRowEdit,
    includeRowDelete,
    showActionMenu = true,
    userRoles,
    uploadPath = 'upload',
    getRowId = null,
    editAction = null,
    recordStatusFilter = null,
    checkboxSelection = true,
    dataType = 'standard',
  } = props
  const theme = useTheme()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [items, setItems] = useState<any[]>([])
  const [open, setOpen] = useState<boolean>(false)
  const [deleteId, setDeleteId] = useState('')
  const { data, isFetching, error, refetch } = useGetItems(
    queryKey[0],
    queryKey[1],
    {
      page,
      pageSize,
      recordStatusFilter,
    }
  )
  const { mutate: editMutate } = useEditMutate(queryKey[0], queryKey[1])
  const { mutate: deleteMutate } = useDeleteMutate(queryKey[0], queryKey[1])
  const { mutate: resendInviteMutate } = useResendUserInvitation(
    queryKey[0],
    queryKey[1]
  )
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])
  const toolBarConfig = { ...defaultToolbarConfig, ...toolBarItems }

  useEffect(() => {
    if (data) setItems(data)
  }, [data])

  const deleteAction = useCallback(
    (id: GridRowId) => {
      deleteMutate(id, {
        onSuccess: () => {
          const item = data?.find((item: any) => item?._id == id)
          if (user?.email == item?.email) {
            localStorage.clear()
            sessionStorage.clear()
            logout({
              logoutParams: {
                returnTo: `${window.location.origin}/login`,
              },
            })
          }
        },
      })
    },
    [deleteMutate]
  )
  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleDelete = (id: any) => {
    setDeleteId(id)
    handleClickOpen()
  }
  useEffect(() => {
    refetch()
  }, [])

  const handleRowEditStart = (
    params: GridRowParams,
    event: MuiEvent<React.SyntheticEvent>
  ) => {
    // Custom logic for row edit start
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    event.defaultMuiPrevented = true
  }

  const handleEditClick = (id: GridRowId) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } })
  }

  const handleSaveClick = (id: GridRowId) => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } })
  }

  const handleCancelClick = (id: GridRowId) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    })
  }

  const handleResendUserInvitation = useCallback(
    (id: GridRowId) => {
      resendInviteMutate(id)
    },
    [resendInviteMutate]
  )

  const columns = useMemo(
    () =>
      ColumnsDef({
        columns: dataColumns,
        deleteHandler:
          includeRowDelete && checkUserRoles(['vtadmin'], userRoles)
            ? handleDelete
            : null,
        rowModesModel,
        handleSaveClick,
        handleCancelClick,
        editModeHandler:
          includeRowEdit && checkUserRoles(['vtadmin'], userRoles)
            ? handleEditClick
            : null,
        editAction,
        viewLink: typeof viewLink === 'function' ? null : viewLink,
        showActionMenu,
        resendUserInvitationHandler:
          dataType === 'invitation' && checkUserRoles(['vtadmin'], userRoles)
            ? handleResendUserInvitation
            : null,
        dataType,
      }),
    [
      dataColumns,
      handleDelete,
      rowModesModel,
      handleSaveClick,
      handleCancelClick,
    ]
  )

  const processRowUpdate = useCallback(
    (row: GridRowModel) => {
      editMutate(row)
      return row
    },
    [editMutate]
  )

  if (error) {
    return <div>An error occurred. Please try realoading.</div>
  }

  return (
    <>
      <DataGridPro
        getRowId={!getRowId ? row => row._id : getRowId}
        rows={items}
        columns={columns}
        pagination
        page={page}
        pageSize={pageSize}
        onPageChange={newPage => setPage(newPage)}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        rowsPerPageOptions={[25]}
        checkboxSelection={checkboxSelection}
        onSelectionModelChange={setSelectionModel}
        selectionModel={selectionModel}
        disableSelectionOnClick
        experimentalFeatures={{
          newEditingApi: checkUserRoles(['vtadmin'], userRoles),
        }}
        rowModesModel={rowModesModel}
        onRowModesModelChange={setRowModesModel}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        editMode='row'
        isCellEditable={isCellEditable}
        components={{
          Toolbar: customToolBar || DataGridEditToolbar,
          RowReorderIcon: CustomMoveIcon,
        }}
        componentsProps={{
          toolbar: {
            setItems,
            setRowModesModel,
            toolBarConfig,
            selectionModel,
            enableBulkActions,
            addAction,
            queryKey,
            setGridFilters,
            setSelectionModel,
            uploadPath,
          },
        }}
        filterModel={{
          items: gridFilters || [],
          linkOperator: GridLinkOperator.And,
        }}
        loading={isFetching}
        sx={{
          backgroundColor: theme.palette.background.paper,
          overflowX: 'scroll',
        }}
        disableColumnFilter
        disableColumnMenu
      />
      <DeleteModal
        open={open}
        setOpen={setOpen}
        deleteAction={() => deleteAction(deleteId)}
      />
    </>
  )
}

export default DataList
